import PropTypes from 'prop-types';

/**
 * Everyone in the room, from the roster the server already broadcasts.
 *
 * ROOM-WIDE, NOT INTEREST-FILTERED, and that is the point. Nameplates show only
 * the handful of people near you, which on an 80x60 map means two players can
 * spend an entire session never learning the other exists. This is the one
 * place where the interest filter works against the feeling the world is
 * chasing, so it deliberately ignores it.
 *
 * Costs nothing to render: ROSTER already arrives on join and leave, and the
 * engine already keeps it. This only surfaces it.
 */
function WhosHere({ players, localShortId }) {
  if (players.length === 0) return null;

  return (
    // A corner overlay on the game rather than a row beneath it. Capped and
    // scrollable so a busy room cannot grow a list down the whole screen.
    <div className="max-h-40 max-w-[14rem] overflow-y-auto rounded-lg border border-gray-800/80 bg-gray-950/80 p-2 backdrop-blur-sm">
      <div className="flex flex-col gap-0.5 text-xs">
        <span className="text-gray-500">
          Here now
          <span className="ml-1 text-gray-600">
            (
            {players.length}
            )
          </span>
        </span>

        {players.map((p) => {
          const isLocal = p.i === localShortId;
          return (
            <span
              key={p.i}
              className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 ${
                isLocal ? 'bg-gray-800 text-gray-200' : 'text-gray-400'
              }`}
            >
              {/*
                The same colour as the shadow under their feet in-game, which
                is what makes this a way to FIND someone rather than just a
                list of names.
              */}
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: p.c }}
                aria-hidden="true"
              />
              {p.n}
              {isLocal && <span className="text-gray-600">(you)</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

WhosHere.propTypes = {
  /** Roster entries: short id, name, colour. */
  players: PropTypes.arrayOf(PropTypes.shape({
    i: PropTypes.number.isRequired,
    n: PropTypes.string,
    c: PropTypes.string,
  })).isRequired,
  /** Which entry is the viewer, so it can be marked. Null before WELCOME. */
  localShortId: PropTypes.number,
};

WhosHere.defaultProps = {
  localShortId: null,
};

export default WhosHere;
